import React, { useEffect, useState } from 'react';
import { useCart } from './CartContext';
import { Link } from 'react-router-dom';
import '../styles/CartPreview.css';
import axios from 'axios';

const formatToIDR = (amount) => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
  }).format(amount);
};

const CartPreview = () => {
  const { 
    cart, 
    removeFromCart, 
    updateQuantity, 
    calculateSubtotal 
  } = useCart();
  
  const [shippingOptions, setShippingOptions] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [courierCode, setCourierCode] = useState('');
  const [destination, setDestination] = useState('');

  const fetchShippingOptions = async () => {
    if (courierCode && destination.trim()) {
      setLoading(true);
      try {
        const response = await axios.get('https://api.binderbyte.com/v1/cost', {
          params: {
            api_key: 'ff7e74f59b1147c63c21c9c43058265c6edb8be3b767e4d10b28cbaa96a57010',
            courier: courierCode,
            origin: 'Pati',
            destination,
            weight: 1
          }
        });

        setLoading(false);
        if (response.data.status === 200) {
          const costs = response.data.data.costs;
          const availableOptions = costs.filter(cost => ['OKE', 'REG', 'UDRREG', 'ECOREG', 'Pos Reguler'].includes(cost.service));
          const lowestCostOption = availableOptions.reduce((prev, curr) => (prev.price < curr.price ? prev : curr), availableOptions[0]);
          setShippingOptions([lowestCostOption]);
          setErrorMessage(null);
        } else {
          setErrorMessage('Courier unavailable');
        }
      } catch (error) {
        setLoading(false);
        setErrorMessage('Courier unavailable');
      }
    }
  };

  useEffect(() => {
    if (errorMessage && courierCode) {
      fetchShippingOptions(); // Retry the request with the new courier code
    }
  }, [errorMessage, courierCode]);

  useEffect(() => {
    fetchShippingOptions();
  }, [courierCode, destination]);

  const selectedShippingOption = shippingOptions.length > 0 ? shippingOptions[0] : null;
  const total = parseInt(calculateSubtotal()) + (selectedShippingOption ? parseInt(selectedShippingOption.price) : 0);

  return (
    <div className="cart-preview">
      <h2>Your Cart</h2>
      {cart.length === 0 ? (
        <p>Your cart is empty</p>
      ) : (
        <>
          {cart.map((item) => (
            <div key={item.id} className="cart-item">
              <img src={item.image} alt={item.name} className="cart-item-image" />
              <div className="cart-item-details">
                <h3>{item.name}</h3>
                <p>{formatToIDR(item.price)}</p>
                <div className="quantity-control">
                  <button 
                    onClick={() => updateQuantity(item.id, item.quantity - 1)}
                    disabled={item.quantity <= 1}
                  >
                    -
                  </button>
                  <span>{item.quantity}</span>
                  <button onClick={() => updateQuantity(item.id, item.quantity + 1)}>
                    +
                  </button>
                </div>
                <button 
                  onClick={() => removeFromCart(item.id)} 
                  className="remove-btn"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
          <div className="cart-summary">
            <div className="cart-summary-item">
              <span>Subtotal: </span>
              <span>{formatToIDR(calculateSubtotal())}</span>
            </div>
            <div className="cart-summary-item">
              <span>Shipping: </span>
              {loading ? (
                <span>Loading...</span>
              ) : errorMessage ? (
                <span>Error</span>
              ) : (
                <>
                  <span>{formatToIDR(selectedShippingOption?.price ?? 0)} | </span>
                  <span>{selectedShippingOption ? `${selectedShippingOption.estimated}` : ''}</span>
                </>
              )}
            </div>
            <div className="cart-summary-item">
              <span>Destination: </span>
              <input 
                type="text" 
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                placeholder="Enter destination"
              />
            </div>
            <div className="cart-summary-item">
              <span>Courier: </span>
              <select 
                value={courierCode}
                onChange={(e) => setCourierCode(e.target.value)}
              >
                <option value="" disabled>Select Courier</option>
                <option value="jne">JNE</option>
                <option value="sap">SAP</option>
                <option value="pos">POS</option>
              </select>
            </div>
            <div className="totals">
              <span>Total: </span>
              <span>{formatToIDR(total)}</span>
            </div>
            <Link to="/checkout" className="btn">Proceed to Checkout</Link>
          </div>
        </>
      )}
      {errorMessage && <p className="error">{errorMessage}</p>}
    </div>
  );
};

export default CartPreview;
